import { useCallback, useMemo } from 'react';

import { AddPartNoteButton } from 'bom/components';
import { IAddPartButtonProps, IPartNoteForm } from 'bom/components/AddPartNote';
import { BomPartViewByType } from 'bom/enums';
import { useElementActions } from 'bom/hooks';
import { IElement, IElementComponentNote } from 'bom/types';
import { useAgGridClientTableContext } from 'core/states';
import { useJobPartNoteActions } from 'jobs/hooks';
import { IComponentNote, IJobPart } from 'jobs/types';

import { JobPartNoteType } from './enums';
import { IJobPartNoteForm } from './types';

interface IProps extends Omit<IAddPartButtonProps, 'upsertPartNoteAction' | 'onSubmitSuccess'> {
  jobId?: number;
  jobPart?: IJobPart;
  element?: IElement;
}

export const AddNoteButton = ({ jobId, jobPart, element, ...rest }: IProps) => {
  const { updateRows } = useAgGridClientTableContext();

  const { upsertJobPartNote } = useJobPartNoteActions();
  const { upsertElementComponentNotes } = useElementActions();

  const availableNoteTypes = useMemo(() => {
    if (jobPart?.id && element?.id) {
      return [JobPartNoteType.DEFAULT, JobPartNoteType.ELEMENT];
    }

    return [JobPartNoteType.DEFAULT];
  }, [element?.id, jobPart?.id]);

  const onJobPartNotesSubmitSuccess = useCallback(
    ({ partNotes }: { partNotes?: IComponentNote[] }) => {
      const prevNotes = jobPart.componentNotes || [];

      const updatedNotes: IComponentNote[] = [...prevNotes];

      if (partNotes) {
        updatedNotes.push(...partNotes);
      }

      updateRows({
        update: [
          {
            ...jobPart,
            componentNotes: updatedNotes,
          },
        ],
      });
    },
    [jobPart, updateRows]
  );

  const onElementComponentNotesSubmitSuccess = useCallback(
    ({ elementComponentNotes }: { elementComponentNotes?: IElementComponentNote[] }) => {
      if (!element?.id || !jobPart?.id) {
        return;
      }

      const elementComponentNotesMap = jobPart.elementComponentNotesMap || {};

      updateRows({
        update: [
          {
            ...jobPart,
            elementComponentNotesMap: {
              ...elementComponentNotesMap,
              [element.id]: [
                ...(elementComponentNotesMap?.[element.id] || []),
                ...elementComponentNotes.map((note) => ({
                  id: note.id,
                  line: note.line,
                  content: note.content,
                })),
              ],
            },
          },
        ],
      });
    },
    [element?.id, jobPart, updateRows]
  );

  const onSubmit = useCallback(
    () =>
      async (formData: IPartNoteForm): Promise<{ success: boolean }> => {
        const data = formData as unknown as IJobPartNoteForm;

        let success = true;

        const jobPartNote = data.notes[JobPartNoteType.DEFAULT];
        const elementNote = data.notes[JobPartNoteType.ELEMENT];

        if (jobPartNote.enabled) {
          const { success: upsertJobPartNoteSuccess, componentNote: updatedNote } =
            await upsertJobPartNote({
              bomId: jobId || jobPart?.bomId,
              bomPartViewBy: {
                group: BomPartViewByType.PART,
              },
              componentNote: {
                content: jobPartNote?.content,
                componentId: jobPart?.componentId,
              },
            });

          if (!upsertJobPartNoteSuccess) {
            success = false;
          }

          if (updatedNote) {
            onJobPartNotesSubmitSuccess({ partNotes: [updatedNote] });
          }
        }

        if (
          jobPart?.id &&
          element?.id &&
          jobPart.elementComponentsMap?.[element.id]?.id &&
          elementNote.enabled
        ) {
          const { success: upsertElementComponentNotesSuccess, elementComponentNotes } =
            await upsertElementComponentNotes({
              elementComponentNotes: [
                {
                  elementComponentId: jobPart.elementComponentsMap[element.id].id,
                  content: elementNote.content,
                },
              ],
            });

          if (!upsertElementComponentNotesSuccess) {
            success = false;
          }

          if (elementComponentNotes) {
            onElementComponentNotesSubmitSuccess({ elementComponentNotes });
          }
        }

        return { success };
      },
    [
      element?.id,
      jobId,
      jobPart?.bomId,
      jobPart?.componentId,
      jobPart?.elementComponentsMap,
      jobPart?.id,
      onJobPartNotesSubmitSuccess,
      upsertJobPartNote,
      onElementComponentNotesSubmitSuccess,
      upsertElementComponentNotes,
    ]
  );

  return (
    <AddPartNoteButton onSubmit={onSubmit} availableNoteTypes={availableNoteTypes} {...rest} />
  );
};
